import React from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, Download } from 'lucide-react';
import { SearchDropdown } from '@/components/SearchDropdown';

export interface PeopleFilterState {
  search: string;
  buyingIntent: string[];
  qualificationStatus: string[];
  datePreset: string;
}

interface PeopleFiltersProps {
  filters: PeopleFilterState;
  onFiltersChange: (patch: Partial<PeopleFilterState>) => void;
  onReset: () => void;
  onExportCsv: () => void;
  isExporting?: boolean;
}

const intentOptions = [
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

const qualificationOptions = [
  { label: 'Qualified', value: 'qualified' },
  { label: 'Unqualified', value: 'unqualified' },
  { label: 'Unknown', value: 'unknown' },
];

function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { label: string; value: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const triggerLabel =
    selected.length === 0
      ? `All ${label}`
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
        : `${label} (${selected.length})`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 shrink-0 justify-between rounded-md border border-border/80 bg-background px-2.5 text-[13px] text-muted-foreground shadow-none hover:bg-accent hover:text-accent-foreground"
        >
          <span className="truncate text-left">{triggerLabel}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-2" align="start">
        <div className="flex flex-col gap-1">
          {options.map(({ label: optLabel, value }) => (
            <label
              key={value}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
            >
              <Checkbox
                checked={selected.includes(value)}
                onCheckedChange={() => toggle(value)}
              />
              {optLabel}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const PeopleFilters: React.FC<PeopleFiltersProps> = ({
  filters,
  onFiltersChange,
  onReset,
  onExportCsv,
  isExporting,
}) => {
  const hasActiveFilters =
    filters.search.length > 0 ||
    filters.buyingIntent.length > 0 ||
    filters.qualificationStatus.length > 0;

  return (
    <div className="flex w-full flex-wrap items-center gap-2">
      <MultiSelectFilter
        label="Intent"
        options={intentOptions}
        selected={filters.buyingIntent}
        onChange={(values) => onFiltersChange({ buyingIntent: values })}
      />

      <MultiSelectFilter
        label="Qualification"
        options={qualificationOptions}
        selected={filters.qualificationStatus}
        onChange={(values) => onFiltersChange({ qualificationStatus: values })}
      />

      <div className="shrink-0">
        <SearchDropdown
          value={filters.search}
          onChange={(value) => onFiltersChange({ search: value })}
          placeholder="Search email..."
          buttonClassName="h-8 rounded-md border border-border/80 bg-background px-2.5 text-[13px] text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          iconClassName="text-icon"
        />
      </div>

      <div className="ml-auto flex items-center gap-2">
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-muted-foreground hover:text-heading" onClick={onReset}>
            Clear
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          className="h-8 rounded-md px-3 text-[13px]"
          onClick={onExportCsv}
          disabled={isExporting}
        >
          <Download className="mr-1.5 h-3.5 w-3.5" />
          {isExporting ? 'Exporting...' : 'Download CSV'}
        </Button>
      </div>
    </div>
  );
};

export default PeopleFilters;
